import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";
import { SERVICES } from "@/data/content";

const QUESTIONS = [
  {
    q: "Τι απασχολεί περισσότερο την επιχείρησή σας αυτή τη στιγμή;",
    options: [
      { label: "Θέλουμε περισσότερες πωλήσεις και νέους πελάτες", pillar: "commercial" },
      { label: "Θέλουμε να ανοίξουμε σε αγορές του εξωτερικού", pillar: "exports" },
      { label: "Η καθημερινή λειτουργία δεν είναι οργανωμένη", pillar: "transformation" },
      { label: "Δεν αξιοποιούμε ακόμη AI και τεχνολογία", pillar: "ai-digital" },
    ],
  },
  {
    q: "Πώς θα περιγράφατε την επιχείρησή σας;",
    options: [
      { label: "Ατομική ή μικρή επιχείρηση", pillar: "strategy" },
      { label: "Μικρομεσαία ή παραγωγική επιχείρηση που μεγαλώνει", pillar: "transformation" },
      { label: "Εμπορική ή εξαγωγική επιχείρηση", pillar: "exports" },
    ],
  },
  {
    q: "Πώς παίρνονται σήμερα οι σημαντικές αποφάσεις;",
    options: [
      { label: "Κυρίως με εμπειρία και διαίσθηση", pillar: "bi-market" },
      { label: "Με κάποια δεδομένα, αλλά χωρίς ενιαία εικόνα", pillar: "bi-market" },
      { label: "Έχουμε δείκτες, αλλά δεν κατευθύνουν την καθημερινότητα", pillar: "strategy" },
    ],
  },
];

export const DiagnosticQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);

  const done = answers.length === QUESTIONS.length;
  const resultPillar = done ? answers.find(Boolean) : null;
  const resultService = resultPillar ? SERVICES.find((s) => s.id === resultPillar) : null;

  const answer = (pillar) => {
    setAnswers((prev) => [...prev, pillar]);
    setStep((s) => s + 1);
  };

  const reset = () => {
    setStep(0);
    setAnswers([]);
  };

  return (
    <div
      data-testid="diagnostic-quiz"
      className="relative overflow-hidden rounded-[32px] bg-white border border-[#0E1B31]/[0.07] p-8 lg:p-12 min-h-[420px] flex flex-col shadow-[0_40px_90px_-50px_rgba(14,27,49,0.4)]"
    >
      <span className="pointer-events-none absolute -right-6 -top-10 font-display text-[12rem] leading-none font-semibold text-[#C26D52]/[0.06] select-none">
        {done ? "✓" : `0${step + 1}`}
      </span>
      <div className="relative flex items-center justify-between">
        <p className="font-mono-brand text-[11px] tracking-[0.3em] uppercase text-[#C26D52]">
          Από πού να ξεκινήσετε
        </p>
        {!done && (
          <div className="flex items-center gap-2" data-testid="quiz-progress">
            {QUESTIONS.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all duration-500 ${i <= step ? "w-8 bg-[#C26D52]" : "w-3 bg-[#0E1B31]/10"}`} />
            ))}
            <span className="ml-2 font-mono-brand text-xs text-[#6B7A8F]">{step + 1} / {QUESTIONS.length}</span>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!done ? (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.35 }}
            className="relative mt-8 flex-1 flex flex-col"
          >
            <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#0E1B31] leading-snug">
              {QUESTIONS[step].q}
            </h3>
            <div className="mt-8 grid grid-cols-1 gap-3">
              {QUESTIONS[step].options.map((opt, i) => (
                <button
                  key={opt.label}
                  data-testid={`quiz-option-${step}-${i}`}
                  onClick={() => answer(opt.pillar)}
                  className="group flex items-center justify-between gap-4 text-left rounded-2xl px-5 py-4 border border-[#0E1B31]/[0.08] bg-[#F8F6F0] text-sm text-[#3B4A5E] hover:border-[#C26D52]/50 hover:bg-[#F7EFE9] hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-20px_rgba(14,27,49,0.4)] transition-all duration-300"
                >
                  {opt.label}
                  <ArrowRight className="h-4 w-4 text-[#C26D52] opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 shrink-0" />
                </button>
              ))}
            </div>
            {step > 0 && (
              <button
                data-testid="quiz-back-button"
                onClick={() => {
                  setStep((s) => s - 1);
                  setAnswers((prev) => prev.slice(0, -1));
                }}
                className="mt-6 inline-flex items-center gap-2 text-xs text-[#6B7A8F] hover:text-[#0E1B31] transition-colors self-start"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Προηγούμενη ερώτηση
              </button>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative mt-8 flex-1 flex flex-col"
            data-testid="quiz-result"
          >
            <p className="text-sm text-[#6B7A8F]">Με βάση τις απαντήσεις σας, ένα λογικό σημείο εκκίνησης είναι:</p>
            <h3 className="mt-4 font-display text-3xl sm:text-4xl font-semibold text-[#C26D52] leading-tight">
              {resultService.title}
            </h3>
            <p className="mt-4 text-base leading-relaxed text-[#3B4A5E] max-w-xl">{resultService.desc}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to={`/contact?interest=${encodeURIComponent(resultService.title)}`}
                data-testid="quiz-result-cta"
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-[#0E1B31] text-[#F8F6F0] text-sm font-semibold hover:bg-[#C26D52] transition-colors duration-300"
              >
                Ας μιλήσουμε για την επιχείρησή σας <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to={`/services#${resultService.id}`}
                data-testid="quiz-result-service-link"
                className="text-sm text-[#2C4A6F] underline-offset-4 hover:underline"
              >
                Δείτε αναλυτικά την υπηρεσία
              </Link>
              <button
                data-testid="quiz-reset-button"
                onClick={reset}
                className="inline-flex items-center gap-2 text-xs text-[#6B7A8F] hover:text-[#0E1B31] transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Επανάληψη
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
